import { Component, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AccessProviderDirective } from 'app/modules/shared/directives/access-provider.directive';
import { TrimSpacesDirective } from 'app/modules/shared/directives/trim-spaces.directive';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { Dropdown, DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { TabViewModule } from 'primeng/tabview';
import { AppSettings } from '../../../shared/app.settings';
import { Countries } from '../../../shared/countries';
import { Country } from '../../../shared/models/country';
import { Language } from '../../../shared/models/language';
import { CommonBindingDataService } from '../../../shared/services/common-binding-data.service';
import { ConfigService } from '../../../shared/services/config.service';
import { UserManagementService } from '../../services/user-management.service';
import { EntityService } from 'app/modules/shared/services/entity.service';
import { ListResponse } from 'app/modules/shared/models/listResponse.models';
import {
  CountryOption,
  RoleOption,
  UserFormData,
  UserDetailsData,
  ParentOption,
  DropdownEvent,
  AddUserFilterEvent,
  MultiSelectComponent,
  UserSearchResponse,
  AttributeData
} from '../../models/user-management';
import { Datum } from 'app/modules/shared/models/listResponse.models';
import { isValidPhoneNumber } from 'libphonenumber-js';

@Component({
  selector: 'app-add-user',
  templateUrl: './add-user.component.html',
  styleUrls: ['./add-user.component.scss', '../../../shared/components/mi-fields/mobile-input/flags.scss'],
  standalone: true,
  imports: [BreadcrumbModule, DialogModule, ButtonModule, TranslateModule, FormsModule, ReactiveFormsModule, InputTextModule, DropdownModule, TrimSpacesDirective, AccessProviderDirective, MultiSelectModule, TabViewModule]
})
export class AddUserComponent implements OnInit {
  @ViewChild('dropdown') dropdown!: Dropdown;
  country: Country;
  language: Language;
  routePath: MenuItem[] = [];
  paramId: number;
  userForm: FormGroup;
  userStatus: string;
  userId: string;
  formSubmitted: boolean = false;
  roleList: RoleOption[] = [];
  eventList: unknown[] = [];
  countries: CountryOption[] | undefined;
  selectedCountry: CountryOption;
  filterIcon = 'pi pi-search';
  showMobileValidationError: boolean = false;
  selectedCountryCode: string;
  originalFormData: UserDetailsData;
  displayDialog: boolean = false;
  isFormInitialized: boolean = false;
  filterText: string = '';
  originalEventList: unknown[] = [];
  timeZoneCode: string;
  parentList: ParentOption[] = [];
  userRoleOption: unknown = AppSettings.USER_ROLE_OPTIONS;
  filterList: any;

  constructor(
    public userManagementService: UserManagementService,
    public cs: CommonBindingDataService,
    private router: Router,
    private fb: FormBuilder,
    private messageService: MessageService,
    private route: ActivatedRoute,
    private configService: ConfigService,
    private entityService: EntityService
  ) { }

  ngOnInit() {
    this.route.queryParams.subscribe(params => this.userId = params?.id);
    this.setRoutePath();
    this.getUserRoles();
    this.setCountries();
    this.initializeForm();
    this.country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));

    this.selectedCountry = this.countries.find(country => country.value === '+61');

  }

  setRoutePath() {
    const manageUserLabel = this.userId ? this.cs.getLabel('manage_users.lbl_edit_user') : this.cs.getLabel('manage_users.lbl_invite_user');
    this.routePath = [
      {
        label: manageUserLabel,
        routerLink: this.userId ? '../../manage-users' : '../../manage-users',
        icon: 'pi pi-arrow-left',
        iconStyle: { 'font-weight': 'bold', 'margin-right': '10px' }
      },
      {
        label: 'Details',
        routerLink: this.userId ? '../../manage-users' : '../../manage-users',
        styleClass: 'breadcrumb-child forward-slash breadcrumb-text',
        style: { 'display': 'flex', 'top': '2px', 'position': 'relative' }
      },
    ];
  }

  alphanumericValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const regex = AppSettings.NAME_PATTERN_WITH_HYPHEN
      const isValid = regex.test(control.value);
      return isValid ? null : { alphanumeric: true };
    };
  }

  initializeForm(): void {
    const emailRegex = new RegExp(
      `^(?!.*\\.\\.)` +
      `(?!\\.)` +
      `(?!.*\\.$)` +
      `(?=.{1,64}@)` +
      `(?=.{6,254}$)` +
      `[^\\s@.][^\\s@]*[^\\s@.]` +
      `@` +
      `(?:[a-zA-Z0-9-]+\\.)+` +
      `[a-zA-Z]{2,}$`
    );

    this.userForm = this.fb.group({
      userRole: ['', [Validators.required]],
      typeId: [''],
      firstName: ['', [Validators.required, Validators.pattern(AppSettings.NAME_PATTERN_WITH_HYPHEN), Validators.minLength(AppSettings.MIN_LENGTH_FOR_FORM_VALIDATION), Validators.maxLength(AppSettings.MAX_LENGTH_FOR_FORM_VALIDATION)]],
      lastName: ['', [Validators.required, Validators.pattern(AppSettings.NAME_PATTERN_WITH_HYPHEN), Validators.minLength(AppSettings.MIN_LENGTH_FOR_FORM_VALIDATION), Validators.maxLength(AppSettings.MAX_LENGTH_FOR_FORM_VALIDATION)]],
      email: ['', [Validators.required, Validators.pattern(emailRegex), this.emailWithLengthValidator]],
      contactNumber: ['', [Validators.pattern(AppSettings.PHONE_PATTERN), Validators.maxLength(AppSettings.MAX_MOBILE_LENGTH), Validators.minLength(AppSettings.MIN_MOBILE_LENGTH)]],
      userType: ['', [Validators.required]],
    });
  }

  emailWithLengthValidator(control: AbstractControl): ValidationErrors | null {
    const email = control.value;
    if (!email) return null;

    const maxTotalLength = 254;
    const maxLocalPartLength = 64;

    if (email.length > maxTotalLength) {
      return { emailMaxLength: true };
    }

    const [localPart] = email.split('@');
    if (localPart && localPart.length > maxLocalPartLength) {
      return { emailLocalPartLength: true };
    }

    return null;
  }

  hasFormChanged(): boolean {
    if (!this.isFormInitialized) return false;
    return JSON.stringify(this.userForm.value) !== JSON.stringify(this.originalFormData);
  }

  getUserRoles() {
    const requestBody = {
      limit: AppSettings.DROPDOWN_ROLE_LIMIT,
      offset: 0,
      searchStr: "",
      filters: [],
      countryCode: '',
      deleted: AppSettings.DELETED_TYPE.ONLY_NON_DELETED,
      forTenantCode: '',
      actionStatus: '',
      status: "both"
    }
    this.userManagementService.searchEntity(requestBody).subscribe((res: UserSearchResponse) => {
      if (res && res.data && Array.isArray(res.data)) {
        this.roleList = res.data.map(item => ({
          roleName: item.roleName as string,
          roleId: item.roleId as string,
          roleCode: item.roleCode as string
        }));
      }
      if (this.userId) {
        this.getUserData();
      } else {
        this.userForm.controls.typeId.disable();
        this.userForm.controls.userRole.disable();
      }
    });
  }

  getname(e: DropdownEvent) {
    const selectedRole = this.roleList.find(role => role.roleId === e.value);
    this.userForm.patchValue({
      roleName: selectedRole.roleName
    });
  }

  saveUser() {
    if (!this.userForm.valid) {
      this.userForm.markAllAsTouched();
      return;
    }
    const selectedUserType = this.roleList.filter(ele => ele.roleCode === this.userForm.controls.userType.value)[0];
    const formData = { ...this.userForm.getRawValue() };
    formData.tenantId = this.country[0]?.tenantId;
    formData.countryCode = this.selectedCountry.value.slice(1, this.selectedCountry.value.length);
    formData.active = true;
    formData.forTenantCode = this.configService.getForTenantCode();
    formData.roleIds = [selectedUserType.roleId];
    switch (selectedUserType.roleCode) {
      case 'SITE_USER':
        formData.userType = 'site';
        break;
      case 'SPONSOR':
        formData.userType = 'sponsor';
        break;
      default:
        formData.userType = selectedUserType.roleName?.toLowerCase();
        break;
    }
    if(!formData.contactNumber) {
      delete formData.contactNumber;
      delete formData.countryCode;
    }
    const userFormData = formData;
    if (this.userId) {
      this.onEditUser(userFormData);
    } else {
      this.onAddUser(userFormData);
    }
  }

  onAddUser(userFormData: UserFormData) {
    this.userManagementService.AddNewUser(userFormData).subscribe(() => {
      this.showSuccessMessage('manage_users.message.msg_user_added_successfully');
      this.router.navigate(["app/manage-users"]);
    }, (error) => {
      const errorMessage = error?.errors?.general?.[0]?.message ?? "An unknown error occurred.";
      this.showErrorMessage(errorMessage);
    });
  }

  onEditUser(userFormData: UserFormData) {
    this.userManagementService.EditUser(this.userId, userFormData).subscribe(() => {
      this.showSuccessMessage('manage_users.message.msg_user_update_successfully');
      this.router.navigate(["app/manage-users"]);
    }, (error) => {
      const errorMessage = error?.errors?.general?.[0]?.message ?? "An unknown error occurred.";
      this.showErrorMessage(errorMessage);
    }
    );
  }

  getUserData() {
    this.userManagementService.getUserDetails(this.userId).subscribe((userData: UserDetailsData) => {
      const userRoleCode = this.roleList.filter(ele => ele.roleId === userData?.rolelist[0]?.roleId)[0].roleCode;
      this.onChangeUserType(userRoleCode);
      this.userForm.patchValue(userData);
      this.originalFormData = { ...userData };
      this.isFormInitialized = true;
      if (userData.rolelist && userData.rolelist.length > 0) {
        const roleId = userData.rolelist[0].roleId;
        this.userForm.get('userType').patchValue(userRoleCode);
        this.originalFormData = { ...userData, userRole: roleId };
      }

      if (userData.contactNumber) {
        const countryCodeFromNumber = `+${userData.countryCode}`;
        const selectedCountry = this.countries.find(c => c.value === countryCodeFromNumber);
        if (selectedCountry) {
          this.selectedCountry = selectedCountry;
          this.selectedCountryCode = selectedCountry.value;
        } else {
          this.selectedCountryCode = '';
        }
      }
      this.userForm.controls.userType.disable();
      this.userForm.controls.typeId.disable();
      this.userForm.controls.userRole.disable();
    });
  }

  setCountries() {
    this.countries = Countries.COUNTRY_CODE;
    const filteredCountries = this.countries.filter(country => country.timezone === this.timeZoneCode);
    this.selectedCountry = filteredCountries[0];
  }

  onFilter(event: AddUserFilterEvent) {
    if (event.filter) {
      this.filterIcon = 'pi pi-times'
    } else {
      this.filterIcon = 'pi pi-search';
    }
  }

  onFilterIconClick() {
    if (this.filterIcon.includes('times')) {
      this.dropdown.filterValue = null;
      this.filterIcon = 'pi pi-search';
    }
  }

  clearSearchField(multiSelect: MultiSelectComponent) {
    multiSelect.filterValue = null;
  }

  selectCountry() {
    this.userForm.get('contactNumber').updateValueAndValidity();
    if (this.userForm.controls.contactNumber.value) {
      const isValid = this.validatePhoneNumber(this.selectedCountry?.value, this.userForm.controls.contactNumber.value);
      this.showMobileValidationError = !isValid;
    } else {
      this.showMobileValidationError = false;
    }
    const control = this.userForm.get('contactNumber');
    if(this.showMobileValidationError) {
      control.setErrors({maxlength: true})
    }
    if (control) {
      control.markAsTouched();
    }
  }

  validatePhoneNumber(countryCode: string, phoneNumber: string): boolean {
    try {
      const formattedCountryCode = countryCode.startsWith('+') ? countryCode : `+${countryCode}`;
      return isValidPhoneNumber(`${formattedCountryCode}${phoneNumber}`);
    } catch (error) {
      return false;
    }
  }

  cancelUserForm() {
    if (this.isFormInitialized && (this.userForm.dirty)) {
      this.displayDialog = true;
    } else {
      this.router.navigate(['app/manage-users']);
    }
  }

  onCancelDialog() {
    this.displayDialog = false;
  }

  onConfirmCancel() {
    this.displayDialog = false;
    this.userForm.patchValue(this.originalFormData);
    if (this.originalFormData.contactNumber) {
      const contactNumberParts = this.originalFormData.contactNumber.split('-');
      if (contactNumberParts.length > 1) {
        const number = contactNumberParts.slice(1).join('-');
        this.userForm.get('contactNumber').setValue(number);
      } else {
        this.userForm.get('contactNumber').setValue(this.originalFormData.contactNumber);
      }
    }
    this.userForm.markAsPristine();
    this.userForm.markAsUntouched();
  }

  private showErrorMessage(labelKey: string) {
    this.messageService.add({
      key: 'tst',
      severity: 'error',
      summary: 'Error',
      detail: this.cs.getLabel(labelKey)
    });
  }

  private showSuccessMessage(labelKey: string) {
    this.messageService.add({
      key: 'tst',
      severity: 'success',
      summary: 'Successful',
      detail: this.cs.getLabel(labelKey),
    });
  }

  onChangeUserType(data: string) {
    this.userForm.controls.userRole.setValue('');
    switch (data) {
      case AppSettings.USER_TYPES.TENANT_ADMIN:
      case AppSettings.USER_TYPES.REVIEWER:
        this.userForm.controls.typeId.reset();
        this.userForm.controls.typeId.disable();
        this.userForm.controls.userRole.disable();
        this.userForm.controls.typeId.removeValidators;
        this.parentList = [];
        break;
      case AppSettings.USER_TYPES.SITE_USER:
        this.userForm.controls.typeId.reset();
        this.getFilterView(AppSettings.ENTITY_CODE.SITE, 'site_status')
        this.userForm.controls.typeId.enable();
        this.userForm.controls.typeId.addValidators([Validators.required]);
        this.userForm.controls.userRole.setValue('site');
        break;
      case AppSettings.USER_TYPES.SPONSOR:
        this.userForm.controls.typeId.reset();
        this.getFilterView(AppSettings.ENTITY_CODE.SPONSOR, 'sponsor_status')
        this.userForm.controls.typeId.enable();
        this.userForm.controls.typeId.addValidators([Validators.required]);
        this.userForm.controls.userRole.disable();
        this.userForm.controls.userRole.setValue('sponsor');
        break;
    }
    this.userForm.controls.typeId.updateValueAndValidity();
  }

  getFilterView(entityCode, attributeCode) {
      this.entityService.getAttributeDefinition(entityCode, AppSettings.VIEW_CODE.ADVANCED_FILTER_VIEW).subscribe((filterResponse: AttributeData) => {
        if (filterResponse) {
          this.filterList = this.cs.getColumns(filterResponse);
          const statusFilterAttrId = this.filterList.find(field => field.attributeCode === attributeCode)?.attributeId;
          this.setParentOption(entityCode, statusFilterAttrId);
        }
      });
    }

  setParentOption(entityCode: string, attrId) {
    this.parentList = [];
    const country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    const requestBody = {
      limit: AppSettings.DROPDOWN_ROLE_LIMIT,
      offset: 0,
      searchStr: "",
      filters: [{attributeId: attrId, attributeValue: ['Active']}],
      countryCode: country[0].countryCode,
      deleted: AppSettings.DELETED_TYPE.ONLY_NON_DELETED,
      forTenantCode: this.configService.getForTenantCode(),
      actionStatus: '',
      status: "active"
    }
    this.entityService.searchEntity(entityCode, requestBody, false).subscribe((res: ListResponse) => {
      this.parentList = res.data.map((ele: Datum) => ({
        label: entityCode === AppSettings.ENTITY_CODE.SITE ? ele.values.site_name as string : ele.values.sponsor_name as string,
        value: ele.id
      })).sort((a, b) => a.label.localeCompare(b.label)) as ParentOption[];
    })
  }
}
